import CacheManager from '@/modules/cache-manager';
import Storage from '@/modules/storage';
import $api from '@/modules/request';
import SizeXtName from '@/modules/sizext-name';
import { wait } from '@/modules/utils';

export const SET_TOKEN = 'SET_TOKEN';
export const SET_LONG_TOKEN = 'SET_LONG_TOKEN';
export const SET_TOKEN_EXPIRY = 'SET_TOKEN_EXPIRY';
export const SET_LONG_TOKEN_EXPIRY = 'SET_LONG_TOKEN_EXPIRY';
export const SET_SHOW_QRCODE = 'SET_SHOW_QRCODE';
export const SET_AUTH = 'SET_AUTH';
export const SET_ME = 'SET_ME';
export const SET_CREDITS = 'SET_CREDITS';
export const SET_OBJ_URL = 'SET_OBJ_URL';
export const SET_FEET_OBJ_URL = 'SET_FEET_OBJ_URL';
export const SET_MOBILE_SCAN_OBJ_URL = 'SET_MOBILE_SCAN_OBJ_URL';
export const SET_MEASUREMENTS = 'SET_MEASUREMENTS';
export const SET_FEET_MEASUREMENTS = 'SET_FEET_MEASUREMENTS';
export const SET_BODY_RECORDS = 'SET_BODY_RECORDS';
export const SET_FEET_RECORDS = 'SET_FEET_RECORDS';
export const SET_IS_FETCHING_RECORDS = 'SET_IS_FETCHING_RECORDS';
export const SET_SCAN_RECORD_DATA = 'SET_SCAN_RECORD_DATA';
export const SET_FRESH_MOBILE_SCAN = 'SET_FRESH_MOBILE_SCAN';
export const SET_POLLING_MOBILE_SCAN_RECORD = 'SET_POLLING_MOBILE_SCAN_RECORD';
export const SET_SHADER_TYPE = 'SET_SHADER_TYPE';
export const SET_RMD_SIZES = 'SET_RMD_SIZES';
export const RESET = 'RESET';
export const BODY_KIND = 0;
export const FEET_KIND = 1;
export const MOBILE_SCAN_PROVIDER = 1;
export const TG3D_MOBILE_SCAN_PROVIDER = 10;

const isMobileScan = (provider) => provider === MOBILE_SCAN_PROVIDER || provider === TG3D_MOBILE_SCAN_PROVIDER;

const POLL_INTERVAL_SECS = 30;
const MAKE_FRESH_BODY_SCAN_SHAPE = (tid, provider) => {
  const now = new Date().toISOString();
  return {
    id: tid,
    tid,
    kind: BODY_KIND,
    status: 0,
    provider,
    icon_url: '',
    created_at: now,
    updated_at: now,
  };
};

const getDefaultState = () => ({
  accessToken: '',
  accessLongToken: '',
  tokenExpiryTimestamp: 0,
  tokenLongExpiryTimestamp: 0,
  tempAccess: false,
  me: {},
  credits: 0,
  objUrl: '',
  feetObjUrl: '',
  mobileScanObjUrl: '',
  measurements: [],
  feetMeasurements: [],
  bodyRecords: [],
  rmdSizes: {},
  feetRecords: [],
  auth: {},
  isFetchingRecords: false,
  freshMobileScan: {},
  isPollingMobileScan: false,
  shaderType: 'default',
});

const user = {
  namespaced: true,
  state: getDefaultState(),
  mutations: {
    [SET_TOKEN](state, token) {
      state.accessToken = token;
    },
    [SET_LONG_TOKEN](state, token) {
      state.accessLongToken = token;
    },
    [SET_TOKEN_EXPIRY](state, timestamp) {
      state.tokenExpiryTimestamp = timestamp;
    },
    [SET_LONG_TOKEN_EXPIRY](state, timestamp) {
      state.tokenLongExpiryTimestamp = timestamp;
    },
    [SET_SHOW_QRCODE](state, tempAccess) {
      state.tempAccess = tempAccess;
    },
    [SET_AUTH](state, auth) {
      state.auth = { ...auth };
    },
    [SET_ME](state, me) {
      state.me = { ...me };
    },
    [SET_CREDITS](state, credits) {
      state.credits = credits;
    },
    [SET_OBJ_URL](state, url) {
      state.objUrl = url;
    },
    [SET_FEET_OBJ_URL](state, url) {
      state.feetObjUrl = url;
    },
    [SET_MOBILE_SCAN_OBJ_URL](state, url) {
      state.mobileScanObjUrl = url;
    },
    [SET_MEASUREMENTS](state, measurements) {
      state.measurements = [...measurements];
    },
    [SET_FEET_MEASUREMENTS](state, measurements) {
      state.feetMeasurements = [...measurements];
    },
    [SET_BODY_RECORDS](state, records) {
      state.bodyRecords = [...records];
    },
    [SET_FEET_RECORDS](state, records) {
      state.feetRecords = [...records];
    },
    [SET_IS_FETCHING_RECORDS](state, isFetching) {
      state.isFetchingRecords = isFetching;
    },
    [SET_SCAN_RECORD_DATA](state, data) {
      const { tid } = data;
      const targetIndex = state.bodyRecords.findIndex(r => r.tid === tid);
      if (targetIndex > -1) {
        state.bodyRecords[targetIndex] = { ...data };
      }
    },
    [SET_FRESH_MOBILE_SCAN](state, scan) {
      state.freshMobileScan = scan;
    },
    [SET_POLLING_MOBILE_SCAN_RECORD](state, started) {
      state.isPollingMobileScan = started;
    },
    [SET_SHADER_TYPE](state, type) {
      state.shaderType = type;
    },
    [SET_RMD_SIZES](state, data) {
      state.rmdSizes = { ...data };
    },
    [RESET](state) {
      Object.assign(state, getDefaultState());
    },
  },
  actions: {
    getAccessToken({ state }) {
      const { accessToken, tokenExpiryTimestamp } = state;
      if (Date.now() >= tokenExpiryTimestamp) {
        return '';
      }
      return accessToken;
    },
    reset({ commit, getters }) {
      const { username } = getters;
      commit(RESET);
      Storage.removeUserAuth();
      Storage.removeUserToken();
      Storage.removeCountryCode();
      return CacheManager.clearUserStuff(username);
    },
    setFreshMobileScan({ commit, dispatch, getters }, tid) {
      const scan = MAKE_FRESH_BODY_SCAN_SHAPE(tid, MOBILE_SCAN_PROVIDER);
      commit(SET_FRESH_MOBILE_SCAN, scan);
      commit(SET_MOBILE_SCAN_OBJ_URL, '');
      CacheManager.setFreshMobileScan(getters.username, scan);
      dispatch('pollFreshMobileScan');
    },
    async pollFreshMobileScan({ commit, state, dispatch, getters }) {
      console.log('polling for FreshMobileScan');
      const { tid } = state.freshMobileScan;
      commit(SET_POLLING_MOBILE_SCAN_RECORD, true);
      const { data } = await $api.user.get(`/api/v1/scan_records/${tid}`);
      const { status } = data;
      console.log('FreshMobileScan status:', status);
      commit(SET_FRESH_MOBILE_SCAN, data);
      CacheManager.setFreshMobileScan(getters.username, data);
      if (status < 3) {
        console.log('keep polling');
        await wait(POLL_INTERVAL_SECS);
        await dispatch('pollFreshMobileScan');
      } else {
        if (status === 3) {
          await dispatch('getScanRecords', {
            kind: BODY_KIND,
            force: true,
            provider: MOBILE_SCAN_PROVIDER,
          });
          await dispatch('setObjUrlByTID', {
            kind: BODY_KIND,
            tid,
            provider: MOBILE_SCAN_PROVIDER,
          });
        } else {
          // TODO: does the obj exist when the status of the scan is not 3?
          commit(SET_MOBILE_SCAN_OBJ_URL, '');
        }
        commit(SET_POLLING_MOBILE_SCAN_RECORD, false);
      }
    },
    setRecords({ commit }, { kind, records }) {
      const mutation = kind === FEET_KIND ? SET_FEET_RECORDS : SET_BODY_RECORDS;
      commit(mutation, records);
    },
    setIsFetchingRecords({ commit }, isFetching) {
      commit(SET_IS_FETCHING_RECORDS, isFetching);
    },
    async setObjUrlByTID({ commit }, { kind, tid, provider }) {
      console.log('set obj url by tid', kind, tid, provider);
      const url = await CacheManager.loadTgs(tid);
      if (kind === FEET_KIND) {
        commit(SET_FEET_OBJ_URL, url);
      } else {
        commit(isMobileScan(provider) ? SET_MOBILE_SCAN_OBJ_URL : SET_OBJ_URL, url);
      }
    },
    async getAllKindsOfScanRecords({ commit, dispatch, getters, state }, force = false) {
      SizeXtName.loadSizeXtName();
      dispatch('setIsFetchingRecords', true);
      console.log('start getting all kinds of records');
      await Promise.all([
        dispatch('getScanRecords', { kind: BODY_KIND, force }),
        dispatch('getScanRecords', { kind: FEET_KIND, force }),
      ]);
      console.log('done getting all kinds of records');
      // restore mobile scan if it exists in cache and start polling
      const { latestMobileScan, username } = getters;
      if (!state.isPollingMobileScan) {
        const cachedScan = await CacheManager.getFreshMobileScan(username);
        if (cachedScan && (!latestMobileScan || latestMobileScan.tid !== cachedScan.tid)) {
          commit(SET_FRESH_MOBILE_SCAN, cachedScan);
          commit(SET_MOBILE_SCAN_OBJ_URL, '');
          dispatch('pollFreshMobileScan');
        } else if (latestMobileScan) {
          await dispatch('setObjUrlByTID', {
            kind: BODY_KIND,
            tid: latestMobileScan.tid,
            provider: MOBILE_SCAN_PROVIDER,
          });
        }
      }
      dispatch('setIsFetchingRecords', false);
    },
    async getScanRecords({ dispatch, getters, commit }, { kind, force, provider }) {
      try {
        const { username, latestMobileScan } = getters;
        let records = await CacheManager.getRecords(username, kind);
        if (!Array.isArray(records) || force) {
          console.log('no cached scan records. start fetching');
          // FIXME: api should accept params to return only mobile scan
          const params = { kinds: kind, limit: 50 };
          if (typeof provider !== 'undefined') {
            params.provider = provider;
          }
          const { data } = await $api.user.get('/api/v1/scan_records', { params });
          records = data.records;
          if (!Array.isArray(records)) return;

          console.log('scan records fetched');
          await CacheManager.setRecords(username, kind, records);
        } else {
          console.log('cached scan records found.');
        }
        dispatch('setRecords', { kind, records });
        const firstNonMobileScan = records.find(r => !r.provider);
        if (firstNonMobileScan) {
          const { tid, provider: p = 0 } = firstNonMobileScan;
          await dispatch('setObjUrlByTID', { kind, tid, provider: p });
        }
        if (!isMobileScan(provider) || !latestMobileScan.tid) return;

        const scan = records.find(r => r.tid === latestMobileScan.tid);
        if (scan) {
          commit(SET_FRESH_MOBILE_SCAN, scan);
          CacheManager.setFreshMobileScan(username, scan);
        }
      } catch (e) {
        console.log(e.message);
      }
    },
    async deleteScanRecord({ commit, state, getters }, { tid, kind }) {
      await $api.user.delete(`/api/v1/scan_records/${tid}`);
      // delete record from store and cache
      const func = r => r.tid !== tid;
      let mutation = SET_BODY_RECORDS;
      let newRecords = state.bodyRecords.filter(func);
      if (kind === FEET_KIND) {
        mutation = SET_FEET_RECORDS;
        newRecords = state.feetRecords.filter(func);
      }
      commit(mutation, newRecords);
      const { username } = getters;
      await CacheManager.setRecords(username, kind, newRecords);
    },
    async clearScanRecords({ dispatch, getters }, kind) {
      const { username } = getters;
      await CacheManager.clearRecords(username, kind);
      dispatch('setRecords', { kind, records: [] });
    },
    async getSizeXt({ state }, { tid, refresh = false }) {
      try {
        const record = state.bodyRecords.find(r => r.tid === tid);
        if (!record) return null;
        if (!record.size_xt || refresh) {
          const { data } = await $api.user.get(`/api/v1/scan_records/${tid}/size_xt?version=4`);
          record.size_xt = data;
        }
        return record.size_xt;
      }
      catch (e) {
        console.log(e.message);
      }
      return null;
    },
    async getRecommendedSizes({ commit }, { tid, locale }) {
      let localeTxt;

      switch (locale) {
        case 'zh-tw':
          localeTxt = 'zh-TW';
          break;
        case 'zh-cn':
          localeTxt = 'zh-CN';
          break;
        default:
          localeTxt = locale;
      }

      const { data } = await $api.user.get(
        `/api/v1/scan_records/${tid}/size_rmd?locale=${localeTxt}`
      );

      commit(SET_RMD_SIZES, data.men || data.women);
    },
    setTempAccess({ commit }, tempAccess) {
      commit('SET_SHOW_QRCODE', tempAccess);
    },
  },
  getters: {
    isFemale: state => state.me.gender === 2,
    isMale: state => state.me.gender === 1,
    credits: state => state.credits,
    objUrl: state => state.objUrl,
    username: state => state.auth.username,
    rmdSizes: state => state.rmdSizes,
    feetObjUrl: state => state.feetObjUrl,
    isAuthenticated: state => !!state.auth.username,
    accessToken: state => state.accessToken,
    accessLongToken: state => state.accessLongToken,
    tempAccess: state => state.tempAccess,
    latestBodyRecord: state => state.bodyRecords[0],
    latestMobileScan: state =>
      state.freshMobileScan.tid
        ? state.freshMobileScan
        : state.bodyRecords.find(record => isMobileScan(record.provider)),
    latestFeetRecord: state => state.feetRecords[0],
    totalRecords: state => state.bodyRecords.length + state.feetRecords.length,
    noRecord: (state, getters) => getters.totalRecords === 0 && !state.isFetchingRecords,
    userHeight: state => state.me.height,
  },
};

export default user;
